import { Fragment, useEffect, useState, startTransition } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import SearchWidget from "@/components/SearchWidget";
import PropertyCard from "@/components/PropertyCard";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { FeaturedReviews } from "@/components/FeaturedReviews";
import { ISLANDS, islandUrl, trs, tr } from "@/lib/translations";
import { Waves, Mountain, Sparkles, Zap, Gift } from "lucide-react";
import { RatingTile } from "@/components/RatingTile";
import { richText } from "@/lib/richText";
import { useSeo, useJsonLd } from "@/lib/useSeo";
import { AdminEditButton } from "@/components/AdminEditButton";
import { HERO_BG, ISLAND_IMAGES, islandSrcSet } from "@/lib/images";
import { positionCss } from "@/components/admin/ImagePositionPicker";
import { useSiteSettings } from "@/context/LanguageContext";
import { HomeComplexes } from "@/components/HomeComplexes";
import { useWelcomeVoucher } from "@/lib/shared";
import { dv } from "@/lib/theme";
import { OffersStrip } from "@/components/OffersStrip";
import { NewPropertiesSection } from "@/components/NewPropertiesSection";
import { HomeTextBlock } from "@/components/HomeTextBlock";
import { HomePlaces } from "@/components/HomePlaces";
import { HomeTips } from "@/components/HomeTips";
import { HomeFaq } from "@/components/HomeFaq";
import { HeroImage } from "@/components/HeroImage";
import { RatingBadge } from "@/components/RatingBadge";
import { resolveHomeSections, isTextSection, textBlockId, homeSectionCount } from "@/lib/homeSections";
import { organizationSchema } from "@/lib/orgSchema";
import { useOffers, offerApplies } from "@/lib/offers";
import { useNear } from "@/lib/useNear";

/** Abdunklung über dem Titelbild: 0–100 % = Verlauf schwächer, über 100 % zusätzlich dunkler. */
export const HeroOverlay = ({ s }) => {
  const v = Number(dv(s, "hero_overlay"));
  return <><div className="absolute inset-0 hero-gradient-overlay" style={{ opacity: Math.min(1, v / 100) }} />{v > 100 && <div className="absolute inset-0 bg-black" style={{ opacity: Math.min(0.7, ((v - 100) / 100) * 0.6) }} />}</>;
};

const IslandTile = ({ isl, label, className, image, alt, position, webp, webpSm, loaded }) => {
  const [broken, setBroken] = useState(false);
  const [ref, near] = useNear();
  const own = !!image && !broken;
  const src = !near ? "" : own ? image : (loaded ? ISLAND_IMAGES[isl] : "");
  return (
  <Link ref={ref} to={islandUrl(isl)} className={`relative overflow-hidden rounded-2xl img-hover-zoom group ${className}`} data-testid={`island-tile-${isl}`}>
    {src ? <picture>
      {own && webp && <source type="image/webp" srcSet={islandSrcSet(webp) || (webpSm ? `${webpSm} 600w, ${webp} 1200w` : webp)} sizes="(min-width: 768px) 33vw, 50vw" />}
      <img src={src} alt={alt || `${label} – Ferienwohnungen & Ferienhäuser`} width="800" height="1000" className="w-full h-full object-cover" style={{ objectPosition: own ? positionCss(position || "top") : "center" }} loading="lazy" decoding="async" onError={() => setBroken(true)} />
    </picture> : <div className="w-full h-full bg-stone-200" />}
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
    <div className="absolute bottom-3 left-3 right-2 md:bottom-4 md:left-4 text-white">
      <div className="text-[10px] uppercase tracking-widest text-gold">Insel</div>
      <div className="font-serif text-xl sm:text-2xl md:text-3xl leading-tight break-words">{label}</div>
    </div>
  </Link>
  );
};

export default function Home() {
  const { t, lang, siteLoaded } = useLang();
  const [properties, setProperties] = useState([]);
  const settings = useSiteSettings();
  const welcome = useWelcomeVoucher();
  const [ratingSummary, setRatingSummary] = useState(null);
  const site = settings;
  const hero = (k, fb) => trs(settings, k, lang) || fb;
  useSeo({ title: hero("seo_title", t.hero.seoTitle), description: hero("seo_description", t.hero.seoDescription), lang, image: settings.home_hero_image || `${window.location.origin}/logo.png` });
  useJsonLd([
    organizationSchema(settings, window.location.origin),
    { "@context": "https://schema.org", "@type": "WebSite", name: "privateHOLIDAY", url: window.location.origin, inLanguage: lang, publisher: { "@id": `${window.location.origin}/#organization` },
      potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/properties?ref={search_term_string}`, "query-input": "required name=search_term_string" } },
  ]);
  const fill = (str) => str.replace(/\{amount\}/g, welcome?.kind === "percent" ? `${welcome.amount} %` : `${welcome?.amount ?? ""} €`).replace(/\{code\}/g, welcome?.code ?? "");
  const fillRich = (str) => {
    const code = welcome?.code;
    return richText(fill(str), (p, k) => { const parts = p.split(code || "\u0000"); return code ? parts.flatMap((x, i) => i ? [<span key={`${k}-${i}`} className="text-signal font-semibold" data-testid="welcome-code">{code}</span>, x] : [x]) : parts; });
  };

  useEffect(() => {
    api.get("/properties/home").then((r) => startTransition(() => setProperties(r.data))).catch(() => setProperties([]));
    api.get("/reviews/summary").then((r) => startTransition(() => setRatingSummary(r.data))).catch(() => setRatingSummary({ average: 0, count: 0 }));
  }, []);
  const offers = useOffers();

  const count = (id) => homeSectionCount(settings, id);
  const featured = properties.filter((p) => p.featured).sort((a, b) => (a.featured_order || 999) - (b.featured_order || 999) || (a.ref_number || 0) - (b.ref_number || 0)).slice(0, count("featured"));
  const islandOrder = [...((settings.home_island_order || []).filter((i) => ISLANDS.includes(i))), ...ISLANDS.filter((i) => !(settings.home_island_order || []).includes(i))];
  const lmOffers = offers.filter((o) => o.kind === "last_minute");
  const lmPct = (p) => Math.max(p.last_minute_percent || 0, ...lmOffers.filter((o) => offerApplies(o, p)).map((o) => o.percent || 0));
  const lastMinute = properties.filter((p) => lmPct(p) > 0).sort((a, b) => lmPct(b) - lmPct(a)).slice(0, count("last_minute"));

  const sectionViews = {
    usps: () => (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 home-section grid sm:grid-cols-2 lg:grid-cols-4 gap-10" data-testid="home-usps">
        {[Waves, Mountain, Sparkles, Gift].map((Icon, i) => {
          const key = ["usp1", "usp2", "usp3"][i];
          const custom = i < 3 ? { title: trs(settings, `${key}_title`, lang) || t.usps[i].title, text: trs(settings, `${key}_text`, lang) || t.usps[i].text } : {};
          return { Icon, ...t.usps[i], ...custom, ...(i === 3 ? { title: trs(settings, "welcome_title", lang) || t.usps[3].title, text: trs(settings, "welcome_text", lang) || t.usps[3].text } : {}) };
        }).filter((u, i) => i !== 3 || welcome).map(({ Icon, title, text }, i) => i === 2 ? (
          <RatingTile key="rating" lang={lang} title={fill(title)} text={fillRich(text)} summary={ratingSummary} />
        ) : (
          <div key={title} className="border-l-2 border-brand pl-6" data-testid={`value-prop-${i}`}>
            <Icon className="w-8 h-8 text-brand mb-4" />
            <h2 className="font-serif text-2xl text-stone-900 mb-2">{fill(title)}</h2>
            <p className="text-sm text-stone-600 leading-relaxed">{fillRich(text)}</p>
          </div>
        ))}
      </section>
    ),
    islands: () => (
      <section id="about" className="max-w-7xl mx-auto px-6 lg:px-10 home-section">
        <div className="home-section-head" data-testid="islands-heading">
          <div className="text-xs uppercase tracking-[0.3em] text-brand">{t.islands.subtitle}</div>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mt-3">{t.islands.title}</h2>
        </div>
        {/* Mobil: 8 gleich große Kacheln in 2 Spalten · Desktop: Teneriffa groß, 4 hochkant, 3 quer */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-4" data-testid="island-grid">
          {islandOrder.map((isl, idx) => (
            <IslandTile key={isl} isl={isl} image={settings[`island_image_${isl}`]} webp={settings[`island_image_${isl}_webp`]} webpSm={settings[`island_image_${isl}_webp_sm`]} position={settings[`island_image_${isl}_position`]} alt={tr(settings[`island_image_${isl}_alt`], lang)} label={t.islands[isl]} loaded={siteLoaded}
              className={idx === 0 ? "aspect-[4/5] md:aspect-auto md:h-full md:col-span-6 md:row-span-2" : idx < 5 ? "aspect-[4/5] md:col-span-3" : "aspect-[4/5] md:aspect-[16/10] md:col-span-4"} />
          ))}
        </div>
      </section>
    ),
    offers: () => <OffersStrip />,
    featured: () => featured.length > 0 && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 home-section" data-testid="featured-section">
        <div className="flex items-end justify-between home-section-head">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand">{t.featured.subtitle}</div>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-900 mt-3">{t.featured.title}</h2>
          </div>
          <Link to="/properties" className="text-sm text-brand underline underline-offset-4 hover:text-brand-hover inline-flex items-center gap-1" data-testid="featured-show-all">{t.featured.showAll || t.nav.properties} →</Link>
        </div>
      <div className="grid md:grid-cols-3 gap-6">
        {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
      </div>
      </section>
    ),
    new: () => <NewPropertiesSection properties={properties} limit={count("new")} />,
    complexes: () => <HomeComplexes limit={count("complexes")} />,
    last_minute: () => lastMinute.length > 0 && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 home-section border-t border-stone-200" data-testid="last-minute-section">
        <div className="flex items-end justify-between home-section-head">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-signal flex items-center gap-2"><Zap className="w-4 h-4" />{t.lastMinute.subtitle}</div>
            <h3 className="font-serif text-4xl md:text-5xl text-stone-900 mt-3">{t.lastMinute.title}</h3>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {lastMinute.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
      </section>
    ),
    reviews: () => <FeaturedReviews limit={count("reviews")} />,
    places: () => <HomePlaces limit={count("places")} />,
    tips: () => <HomeTips limit={count("tips")} />,
    faq: () => <HomeFaq items={settings.home_faq} />,
    recent: () => <RecentlyViewed limit={count("recent")} />,
  };

  return (
    <div>
      <AdminEditButton kind="home" label="Startseite im Admin bearbeiten (Einstellungen)" />
      {/* Hero */}
      <section className="relative flex items-end overflow-hidden" style={{ minHeight: `${dv(site, "hero_height")}vh`, "--hero-h": `${dv(site, "hero_height")}vh` }} data-testid="home-hero">
        <HeroImage settings={settings} alt={tr(settings.home_hero_image_alt, lang) || "Ferienwohnungen und Ferienhäuser auf den Kanarischen Inseln – privateHOLIDAY"} />
        <HeroOverlay s={site} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-16 pt-32 w-full">
          <div className="max-w-4xl">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-white/90 font-semibold mb-6"
                  data-testid="hero-badge">
              {hero("hero_badge", t.hero.badge)}
            </span>
            <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-hero leading-[1.1] tracking-tight" data-testid="hero-title">
              {hero("hero_title", t.hero.title)}
              <br />
              <span className="italic text-sun text-3xl sm:text-4xl lg:text-5xl">{hero("hero_accent", t.hero.titleAccent)}</span>
            </h1>
            <p className="text-white/85 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
              {hero("hero_subtitle", t.hero.subtitle)}
            </p>
          </div>

          <div className="mt-12 max-w-5xl">
            <div className="mb-3 flex justify-end text-white" data-testid="hero-rating-wrap"><RatingBadge lang={lang} testId="hero-rating-badge" inline className="w-full justify-center sm:w-auto" /></div>
            <SearchWidget />
          </div>
        </div>
      </section>

      {/* Abschnitte in Admin-Reihenfolge (Design → Startseite → Abschnitte) */}
      {resolveHomeSections(settings).filter((sec) => sec.on).map((sec) => {
        if (isTextSection(sec.id)) {
          const b = (settings.home_text_blocks || []).find((x) => x.id === textBlockId(sec.id));
          return b ? <HomeTextBlock key={sec.id} block={b} /> : null;
        }
        return <Fragment key={sec.id}>{sectionViews[sec.id]?.()}</Fragment>;
      })}
    </div>
  );
}
